import { FC, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { CashAccount } from '@shared/api/cashAccount/cashAccount.interface';
import { Button } from '@shared/components/Button';
import ResizableBox from '@shared/components/ResizableBox';
import { Icon } from '@shared/components/icons';
import { notificateSuccess } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { unsecuredCopyToClipboard } from '@shared/utils/clipboard.util';

const DURATION = 0.3;

interface Props {
  cashAccount?: CashAccount;
  additionalCashAccountList?: CashAccount[];
  forMember?: boolean;
  cashAccountEditOpenId?: string;
  emptyAccountMessage?: string;
  accountExpose?: boolean;
}

export const MemberMoney: FC<Props> = function MemberMoney({
  cashAccount,
  forMember,
  cashAccountEditOpenId,
  additionalCashAccountList,
  emptyAccountMessage,
  accountExpose,
}: Props) {
  const { ChevronDown, ChevronUp, MoneyBag } = Icon();
  const [fold, setFold] = useState(!accountExpose);
  const setCashAccountEditOpen = useSetRecoilState(
    simpleOpenAtomFamily(cashAccountEditOpenId),
  );
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);

  const verifiedAccountList = useMemo(
    () =>
      [cashAccount, ...(additionalCashAccountList ?? [])].filter(
        (account): account is CashAccount => !!account?.isVerified,
      ),
    [cashAccount, additionalCashAccountList],
  );

  const renderCashAccount = (account: CashAccount, index: number) => {
    return (
      <div
        key={account._id}
        className={`flex flex-col gap-2 text-15 font-medium ${
          index > 0 ? 'mt-3 border-t pt-3' : ''
        }`}
      >
        <div className="relative flex justify-between gap-1">
          <p>{account.name}</p>
          <p
            className="absolute right-0 top-0 cursor-pointer rounded-md border bg-white px-2 py-0.5 text-[14px] font-bold theme-text-main theme-border-main"
            onClick={async (): Promise<void> => {
              navigator.clipboard
                .writeText(`${account.bank} ${account.account}`)
                .then(() => {
                  notificateSuccess(notifiacationInstance, '계좌복사 완료');
                })
                .catch(() => {
                  unsecuredCopyToClipboard(
                    `${account.bank} ${account.account}`,
                    '계좌복사 완료',
                    notifiacationInstance,
                  );
                });
            }}
          >
            계좌복사
          </p>
        </div>
        <div className="flex gap-5">
          <p className="break-keep">{account.bank}</p>
          <p className="break-keep">{account.account}</p>
        </div>
      </div>
    );
  };

  // 본인 계좌가 없어도 추가 계좌가 하나라도 있으면 빈 안내는 띄우지 않는다.
  const emptyContent = useMemo(() => {
    const message =
      emptyAccountMessage ||
      (forMember
        ? '아직 등록된 부의금 계좌가 없습니다.\n아래 버튼으로 등록해 주세요.'
        : '아직 안내된 부의금 계좌가 없습니다.');
    return (
      <div className="mx-auto max-w-[280px] py-3 text-center">
        <div className="mb-2 flex justify-center">
          <span className="inline-block h-px w-10 bg-gray-300" />
        </div>
        <p className="whitespace-pre-line break-keep text-[15px] font-semibold leading-relaxed tracking-wide text-gray-700">
          {message}
        </p>
        <div className="mt-2 flex justify-center">
          <span className="inline-block h-px w-10 bg-gray-300" />
        </div>
      </div>
    );
  }, [emptyAccountMessage, forMember]);

  const memberCashAccountContent = useMemo(() => {
    if (forMember) {
      return (
        <div className="border-t pt-3">
          <div className="center-box">
            <Button
              className="button-no-border p-2 theme-text-3 theme-bg-main"
              onClick={() => {
                setCashAccountEditOpen(true);
              }}
            >
              {verifiedAccountList.length > 0 ? '부의금정보수정' : '부의금 계좌 등록'}
            </Button>
          </div>
        </div>
      );
    }
  }, [forMember, setCashAccountEditOpen, verifiedAccountList.length]);

  return (
    <div className="bg-white px-2">
      <div
        className={`flex items-center justify-between py-2 ${
          fold ? 'cursor-pointer' : ''
        }`}
        onClick={() => {
          if (fold) {
            setFold(!fold);
          }
        }}
      >
        <div className="center-box">
          <MoneyBag className="wh-10" />
          <p className="font-bold text-gray-500">마음 전하실 곳(부의금)</p>
        </div>
        <div
          className="cursor-pointer"
          onClick={() => {
            setFold(!fold);
          }}
        >
          {fold ? <ChevronDown /> : <ChevronUp />}
        </div>
      </div>
      <ResizableBox duration={DURATION}>
        {!fold && (
          <div className="space-y-2 border-gray-300 px-4 pb-3">
            {verifiedAccountList.length > 0
              ? verifiedAccountList.map(renderCashAccount)
              : emptyContent}
            {memberCashAccountContent}
          </div>
        )}
      </ResizableBox>
    </div>
  );
};
